<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-3 sm:p-4">
    <div class="bg-white rounded-3xl max-w-3xl w-full shadow-2xl relative overflow-hidden animate-fade-in my-auto max-h-[88vh] flex flex-col">
      <!-- Sticky Header -->
      <div class="flex justify-between items-center px-6 py-3.5 border-b border-gray-100 shrink-0 bg-white">
        <div>
          <div class="flex items-center gap-2">
            <h2 class="text-lg font-bold text-gray-900">Liquidar y Pagar Nómina</h2>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800">
              Conectado con /shoppings
            </span>
          </div>
          <p class="text-xs text-gray-500">Selecciona el personal y el período de turnos a pagar.</p>
        </div>
        <button 
          type="button"
          class="text-gray-400 hover:text-gray-600 text-lg p-1 transition-colors cursor-pointer" 
          @click="$emit('close')"
        >
          <i class="fa fa-times" />
        </button>
      </div>

      <!-- Scrollable Form Body -->
      <form id="settlementFormElement" class="flex-1 overflow-y-auto px-6 py-3.5 space-y-3" @submit.prevent="handleSubmit">
        <!-- Paso 1: Selección de Empleado y Fechas -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5 bg-gray-50 p-2.5 rounded-xl border border-gray-200">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Empleado a Liquidar *</label>
            <select
              v-model="form.employee_id"
              required
              class="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs focus:ring-orange-500 focus:border-orange-500 font-bold"
              @change="fetchPendingShifts"
            >
              <option value="" disabled>Selecciona empleado...</option>
              <option v-for="emp in allActiveEmployees" :key="emp.id" :value="emp.id">
                {{ emp.name }} ({{ emp.role_or_job || 'Personal' }})
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Desde Fecha *</label>
            <input
              v-model="form.start_date"
              type="date"
              required
              class="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs focus:ring-orange-500 focus:border-orange-500 font-semibold"
              @change="fetchPendingShifts"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Hasta Fecha *</label>
            <input
              v-model="form.end_date"
              type="date"
              required
              class="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs focus:ring-orange-500 focus:border-orange-500 font-semibold"
              @change="fetchPendingShifts"
            />
          </div>
        </div>

        <!-- Paso 2: Lista de Turnos Pendientes Encontrados -->
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="text-xs font-bold uppercase tracking-wider text-gray-700">
              Turnos Pendientes de Pago Encontrados ({{ pendingShiftsLoaded.length }})
            </label>
            <span v-if="loadingShifts" class="text-xs font-bold text-orange-600 flex items-center gap-1">
              <i class="fa fa-spinner fa-spin" /> Cargando...
            </span>
          </div>

          <div v-if="!form.employee_id" class="p-4 text-center text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <i class="fa fa-user-clock text-2xl mb-1 text-gray-300" />
            <p class="text-xs font-medium">Selecciona un empleado arriba para cargar sus turnos pendientes.</p>
          </div>

          <div v-else-if="pendingShiftsLoaded.length === 0 && !loadingShifts" class="p-4 text-center text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <i class="fa fa-check-circle text-2xl mb-1 text-emerald-500" />
            <p class="text-xs font-semibold text-gray-700">No hay turnos pendientes en este rango de fechas.</p>
          </div>

          <div v-else class="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100 max-h-36 overflow-y-auto">
            <div
              v-for="shift in pendingShiftsLoaded"
              :key="shift.id"
              class="p-2.5 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors"
              :class="isShiftSelected(shift.id) ? 'bg-orange-50/20' : 'opacity-60'"
            >
              <div class="flex items-center gap-2.5 min-w-0">
                <input
                  v-model="form.shift_ids"
                  type="checkbox"
                  :value="shift.id"
                  class="w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500 cursor-pointer"
                />
                <div class="truncate">
                  <div class="font-bold text-gray-900 text-xs">
                    {{ formatDate(shift.date) }} • {{ shift.shift_type }}
                  </div>
                  <div class="text-[11px] text-gray-500 truncate">
                    {{ shift.hours ? shift.hours + ' hrs - ' : '' }}{{ shift.notes || 'Turno ordinario' }}
                  </div>
                </div>
              </div>

              <div class="font-bold text-gray-900 text-xs shrink-0">
                {{ formatUSD(shift.amount) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Paso 3: Ajustes Financieros (Base, Bonos, Deducciones) -->
        <div class="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-2.5">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <div>
              <label class="block text-xs font-bold text-gray-700 mb-0.5">Subtotal Turnos ({{ selectedSettlementShiftsCount }})</label>
              <input
                :value="formatUSD(selectedSettlementBaseAmount)"
                readonly
                class="w-full px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-xs font-bold text-gray-800"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-emerald-700 mb-0.5">+ Bonos / Extras ($)</label>
              <input
                v-model.number="form.bonus_amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                class="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-emerald-600 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-red-700 mb-0.5">- Deducciones / Anticipos ($)</label>
              <input
                v-model.number="form.deduction_amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                class="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-red-600 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>

          <!-- Gran Total a Pagar -->
          <div class="p-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl text-white flex items-center justify-between">
            <div>
              <div class="text-[11px] uppercase font-bold text-orange-100">Total a Liquidar</div>
              <div class="text-[11px] text-orange-100">
                {{ selectedSettlementShiftsCount }} turnos seleccionados
              </div>
            </div>
            <div class="text-right">
              <div class="text-2xl font-bold tracking-tight">
                {{ formatUSD(calculatedSettlementTotal) }}
              </div>
              <div class="text-[11px] font-bold text-orange-100">
                {{ formatBs(calculatedSettlementTotal) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Paso 4: Datos del Pago -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-0.5">Método de Pago *</label>
            <select
              v-model="form.payment_method"
              required
              class="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold"
            >
              <option v-for="method in paymentMethods" :key="method" :value="method">
                {{ method }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-0.5">Fecha del Pago *</label>
            <input
              v-model="form.payment_date"
              type="date"
              required
              class="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-0.5">Referencia Bancaria</label>
            <input
              v-model="form.payment_reference"
              type="text"
              placeholder="Ej. 984512"
              class="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-mono focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div class="sm:col-span-3">
            <label class="block text-xs font-bold text-gray-700 mb-0.5">Observaciones / Concepto de Pago</label>
            <input
              v-model="form.notes"
              type="text"
              placeholder="Ej. Pago de quincena, corte de 15 turnos..."
              class="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        <!-- Warning Box -->
        <div class="p-2.5 bg-amber-50 rounded-xl border border-amber-200 text-xs text-amber-900 flex items-center gap-2">
          <i class="fa fa-shield-alt text-amber-600 text-sm shrink-0" />
          <div>
            <strong>Prevención de doble pago:</strong> Los turnos quedarán marcados como <strong>Pagados</strong> y se creará el egreso en <strong>/shoppings</strong>.
          </div>
        </div>
      </form>

      <!-- Sticky Footer -->
      <div class="px-6 py-3 bg-gray-50 border-t border-gray-100 shrink-0 flex items-center justify-end gap-3 rounded-b-3xl">
        <button
          type="button"
          class="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-medium text-xs hover:bg-gray-100 transition-colors cursor-pointer"
          @click="$emit('close')"
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="settlementFormElement"
          :disabled="submitting || form.shift_ids.length === 0"
          class="px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium text-xs shadow-md transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
        >
          {{ submitting ? 'Procesando Pago...' : `Confirmar y Liquidar (${formatUSD(calculatedSettlementTotal)})` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SettlementModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    targetEmployee: {
      type: Object,
      default: null,
    },
    allActiveEmployees: {
      type: Array,
      default: () => [],
    },
    paymentMethods: {
      type: Array,
      default: () => ['Efectivo Dolar', 'Pago Móvil', 'Transferencia Bs', 'Zelle'],
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    currentExchangeRate: {
      type: Number,
      default: 1.0,
    },
  },

  emits: ['close', 'submit'],

  data() {
    return {
      loadingShifts: false,
      pendingShiftsLoaded: [],
      form: {
        employee_id: '',
        start_date: this.getDefaultStartDate(),
        end_date: new Date().toISOString().split('T')[0],
        payment_date: new Date().toISOString().split('T')[0],
        shift_ids: [],
        bonus_amount: 0,
        deduction_amount: 0,
        payment_method: this.paymentMethods?.[0] || 'Efectivo Dolar',
        payment_reference: '',
        notes: '',
      },
    }
  },

  computed: {
    selectedSettlementShiftsCount() {
      return this.form.shift_ids.length
    },

    selectedSettlementBaseAmount() {
      return this.pendingShiftsLoaded
        .filter(s => this.form.shift_ids.includes(s.id))
        .reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0)
    },

    calculatedSettlementTotal() {
      const base = this.selectedSettlementBaseAmount
      const bonus = parseFloat(this.form.bonus_amount) || 0
      const deduction = parseFloat(this.form.deduction_amount) || 0
      return Math.max(0, base + bonus - deduction)
    },
  },

  watch: {
    isOpen(val) {
      if (val) {
        const empId = this.targetEmployee ? this.targetEmployee.id : (this.allActiveEmployees?.[0]?.id || '')
        this.form = {
          employee_id: empId,
          start_date: this.getDefaultStartDate(),
          end_date: new Date().toISOString().split('T')[0],
          payment_date: new Date().toISOString().split('T')[0],
          shift_ids: [],
          bonus_amount: 0,
          deduction_amount: 0,
          payment_method: this.paymentMethods?.[0] || 'Efectivo Dolar',
          payment_reference: '',
          notes: '',
        }
        if (empId) {
          this.fetchPendingShifts()
        }
      }
    },
  },

  methods: {
    getDefaultStartDate() {
      const d = new Date()
      d.setDate(d.getDate() - 15)
      return d.toISOString().split('T')[0]
    },

    fetchPendingShifts() {
      if (!this.form.employee_id) {
        this.pendingShiftsLoaded = []
        this.form.shift_ids = []
        return
      }

      this.loadingShifts = true
      const url = `/paysheet/pending-shifts/${this.form.employee_id}?date_from=${this.form.start_date}&date_to=${this.form.end_date}`

      fetch(url)
        .then(res => res.json())
        .then(data => {
          this.pendingShiftsLoaded = data.shifts || []
          this.form.shift_ids = this.pendingShiftsLoaded.map(s => s.id)
          this.loadingShifts = false
        })
        .catch(() => {
          this.loadingShifts = false
        })
    },

    isShiftSelected(shiftId) {
      return this.form.shift_ids.includes(shiftId)
    },

    handleSubmit() {
      this.$emit('submit', {
        ...this.form,
        total: this.calculatedSettlementTotal,
        shiftsCount: this.selectedSettlementShiftsCount,
      })
    },

    formatUSD(val) {
      const num = parseFloat(val) || 0
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatBs(val) {
      const num = (parseFloat(val) || 0) * (this.currentExchangeRate || 1)
      return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Bs.'
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const parts = dateStr.split('T')[0].split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateStr
    },
  },
}
</script>
